export type GridViewStatus = 'active' | 'deleted'
export type GridViewConfigKind = 'TEAM_VIEW' | 'USER_STATE'
export type GridViewSelectedRef =
  | `team:${string}`
  | `system:${string}`
  | `explicit:system:${string}`
  | `inactive:team:${string}`
  | `inactive:system:${string}`
  | `inactive:explicit:system:${string}`
  | 'inactive:default'
  | 'personal'
  | 'default'

export const DEFAULT_GRID_VIEW_REF: GridViewSelectedRef = 'default'
export const PERSONAL_GRID_VIEW_REF: GridViewSelectedRef = 'personal'
const INACTIVE_PERSONAL_GRID_VIEW_REF_PREFIX = 'inactive:'

export const buildTeamGridViewRef = (viewId: string): GridViewSelectedRef =>
  `team:${viewId}`

export const buildSystemGridViewRef = (viewKey: string): GridViewSelectedRef =>
  `system:${viewKey}`

export const buildExplicitSystemGridViewRef = (
  viewKey: string,
): GridViewSelectedRef => `explicit:system:${viewKey}`

export const buildInactivePersonalGridViewRef = (
  selectedViewRef: GridViewSelectedRef,
): GridViewSelectedRef => {
  if (
    selectedViewRef === PERSONAL_GRID_VIEW_REF ||
    selectedViewRef.startsWith(INACTIVE_PERSONAL_GRID_VIEW_REF_PREFIX)
  ) {
    return DEFAULT_GRID_VIEW_REF
  }

  return `${INACTIVE_PERSONAL_GRID_VIEW_REF_PREFIX}${selectedViewRef}` as GridViewSelectedRef
}

export const buildGridViewPreferenceId = ({
  userID,
  organizationID,
  scopeKey,
}: {
  userID: string
  organizationID: string
  scopeKey: string
}) => `${userID}:${organizationID}:${scopeKey}`

export const parseGridViewSelectedRef = (
  selectedViewRef: string | null | undefined,
):
  | { type: 'team'; id: string }
  | { type: 'system'; key: string; isExplicitSelection?: true }
  | { type: 'personal' }
  | { type: 'team'; id: string; isPersonalDraftInactive: true }
  | {
      type: 'system'
      key: string
      isExplicitSelection?: true
      isPersonalDraftInactive: true
    }
  | { type: 'default'; isPersonalDraftInactive: true }
  | { type: 'default' } => {
  if (selectedViewRef === PERSONAL_GRID_VIEW_REF) {
    return { type: 'personal' }
  }

  if (selectedViewRef?.startsWith(INACTIVE_PERSONAL_GRID_VIEW_REF_PREFIX)) {
    const parsed = parseGridViewSelectedRef(
      selectedViewRef.slice(INACTIVE_PERSONAL_GRID_VIEW_REF_PREFIX.length),
    )
    if (parsed.type === 'personal') {
      return { type: 'default', isPersonalDraftInactive: true }
    }
    return { ...parsed, isPersonalDraftInactive: true }
  }

  if (selectedViewRef?.startsWith('explicit:system:')) {
    const key = selectedViewRef.slice('explicit:system:'.length)
    return key.length > 0
      ? { type: 'system', key, isExplicitSelection: true }
      : { type: 'default' }
  }

  if (selectedViewRef?.startsWith('team:')) {
    const id = selectedViewRef.slice('team:'.length)
    return id.length > 0 ? { type: 'team', id } : { type: 'default' }
  }

  if (selectedViewRef?.startsWith('system:')) {
    const key = selectedViewRef.slice('system:'.length)
    return key.length > 0 ? { type: 'system', key } : { type: 'default' }
  }

  return { type: 'default' }
}

export type GridViewConfigItem = {
  id: string
  kind: GridViewConfigKind
  organizationID: string
  scopeKey: string
  userID?: string | null
  name?: string | null
  description?: string | null
  selectedViewRef?: string | null
  payload?: string | null
  draftPayload?: string | null
  payloadVersion: string
  gridSchemaVersion: string
  status: GridViewStatus | string
  isDefault?: boolean | null
  createdByUserID?: string | null
  updatedByUserID?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  revision?: number | null
  source?: string | null
}

export type GridViewItem = {
  id: string
  organizationID: string
  scopeKey: string
  name: string
  description?: string | null
  payload: string
  payloadVersion: string
  gridSchemaVersion: string
  status: GridViewStatus | string
  isDefault?: boolean | null
  createdByUserID: string
  updatedByUserID: string
  createdAt?: string | null
  updatedAt?: string | null
  source?: string | null
}

export type GridViewPreferenceItem = {
  id: string
  userID: string
  organizationID: string
  scopeKey: string
  selectedViewRef?: GridViewSelectedRef | string | null
  draftPayload?: string | null
  payloadVersion: string
  gridSchemaVersion: string
  status: GridViewStatus | string
  createdAt?: string | null
  updatedAt?: string | null
  revision?: number | null
  source?: string | null
}
